import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input } from 'antd';

import Image from 'global/Image';
import If from 'global/Common/If';

import useMultiPortfolioStore from 'lib/stores/multiPorto';
import usePortfolioSelect from 'features/multi-porto/hooks/usePortfolioSelect';
import useModalStore, {
  ModalNames,
} from 'features/multi-porto/stores/modal.store';
import {
  formatCurrency,
  adjustCursorPosition,
} from 'features/securities/withdrawal/utils';
import { getRoundDownValue } from 'features/securities/utils';

import BaseSecuritiesModal from 'global/Securities/components/Modal/BaseSecuritiesModal';
import type { BaseSecuritiesModalProps } from 'global/Securities/components/Modal/BaseSecuritiesModal';
import { BalanceScopeEnum } from 'features/securities/withdrawal/types';

import PortfolioSelect from '../TransferPortfolioSelect';

import {
  Container,
  InputStyled,
  NextButton,
  InputError,
  BalanceContainer,
  BalanceButton,
  SelectContainer,
  SwitchButton,
  BalanceButtonLoader,
} from './cash.styled';

const TransferCash = ({
  onCancel = () => {},
  onSubmit = () => {},
  ...props
}: BaseSecuritiesModalProps) => {
  const [form] = Form.useForm();
  const [amount, setAmount] = useState<string | null>(null);
  const [error, setError] = useState<string>('');

  const amountInputRef = useRef<Input>(null);

  const {
    fromPortfolio,
    fromPortfolioOptions,
    toPortfolio,
    toPortfolioOptions,
    disabledSelect,
    onChangeFromPortfolio,
    onChangeToPortfolio,
    onSwitchPortfolio,
    onReset,
  } = usePortfolioSelect({
    visible: props.visible,
    form,
  });

  const setModal = useModalStore((s) => s.openModal);

  // cash transfer form
  const moveCashForm = useMultiPortfolioStore((s) => s.moveCashForm);
  const setMoveCashForm = useMultiPortfolioStore((s) => s.setMoveCashForm);

  // withdrawable balance
  const witdrawableLoading = useMultiPortfolioStore(
    (s) => s.withDrawableData.isLoading,
  );
  const availableBalance = useMultiPortfolioStore(
    (s) => s.withDrawableData.data?.balance?.net_withdrawable || 0,
  );
  const getWithdrawableBalance = useMultiPortfolioStore(
    (s) => s.getWithdrawable,
  );
  const resetWithdrawableBalance = useMultiPortfolioStore(
    (s) => s.resetWithdrawable,
  );

  const disabledSubmit = useMemo(
    () =>
      !amount ||
      Number(amount) <= 0 ||
      !!error ||
      !fromPortfolio ||
      !toPortfolio,
    [amount, error, fromPortfolio, toPortfolio],
  );

  const formatInputNumber = (value: string): string | undefined => {
    if (!value) return value;

    // Remove non-numeric characters (including '.' or ',') from the input
    const integerPart = value.replace(/\D/g, '');

    // Format the integer part with commas
    const formattedInteger = new Intl.NumberFormat('en-US').format(
      Number(integerPart),
    );

    // Return the formatted integer without handling decimals
    return formattedInteger;
  };

  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = e.target;
    const formattedValue = formatInputNumber(value);
    setAmount(formattedValue);

    const cursorPosition = selectionStart || 0;
    adjustCursorPosition(e, cursorPosition, value, formattedValue);

    // Remove commas and convert to number for comparison
    const numericValue = parseFloat(value.replace(/,/g, ''));

    const exceedBalance = numericValue > availableBalance;
    const lessThanMinimum = numericValue < 50000;

    // Check if input exceeds available balance
    if (exceedBalance && !lessThanMinimum) {
      setError('Amount you entered exceeds the transferable balance');
    } else if (lessThanMinimum) {
      setError('Minimum transfer Rp50,000');
    } else {
      setError('');
    }
  };

  const handleReset = () => {
    onReset();
    setAmount('');
    setError('');
  };

  const handleSaveForm = () => {
    const unformattedAmount = amount.replace(/,/g, '');
    const numberAmount = parseFloat(unformattedAmount);

    setMoveCashForm({
      from_acc_no: fromPortfolio || '',
      from_acc_name: fromPortfolioOptions.find(
        (subAccount) => subAccount.value === fromPortfolio,
      )?.label,
      to_acc_no: toPortfolio || '',
      to_acc_name: toPortfolioOptions.find(
        (subAccount) => subAccount.value === toPortfolio,
      )?.label,
      amount: Number.isNaN(numberAmount) ? 0 : numberAmount,
    });
  };

  const handleSubmit = () => {
    onSubmit();
    handleSaveForm();
  };

  const handleCancel = (e: MouseEvent<HTMLElement>) => {
    onCancel(e);
    handleReset();
    resetWithdrawableBalance();
  };

  const handleClickBalanceDetail = () => {
    if (!fromPortfolio) return;
    setModal(ModalNames.TRANSFER_CASH_BALANCE_REVIEW);
    handleSaveForm();
  };

  // Focus on amount input when fromPortfolio and toPortfolio are set
  useEffect(() => {
    if (fromPortfolio && toPortfolio) {
      amountInputRef.current?.focus();
    }
  }, [fromPortfolio, toPortfolio, amountInputRef]);

  // Get withdrawable balance when fromPortfolio is set
  useEffect(() => {
    if (fromPortfolio) {
      getWithdrawableBalance(BalanceScopeEnum.SELECTED_ACCOUNT, fromPortfolio);
    }
  }, [fromPortfolio, getWithdrawableBalance]);

  // Reset form when saved fromPortfolio is empty
  useEffect(() => {
    if (!moveCashForm.from_acc_no) handleReset();
  }, [moveCashForm.from_acc_no]);

  return (
    <BaseSecuritiesModal
      title="Transfer Cash"
      hideSubmit
      onCancel={handleCancel}
      className="modal-multi-porto"
      transitionName=""
      {...props}
    >
      <Container onClick={(e) => e.stopPropagation()}>
        <Form form={form} layout="vertical">
          <SelectContainer>
            <SwitchButton
              onClick={() => {
                onSwitchPortfolio();
                setError('');
                setAmount('');
                form.resetFields(['amount']);
              }}
            >
              <Image src="/icon/multi-porto/transfer-switch.svg" alt="" />
            </SwitchButton>

            <PortfolioSelect
              label="From Portfolio"
              name="fromPortfolio"
              value={fromPortfolio}
              options={fromPortfolioOptions}
              onChange={(value) => {
                setError('');
                onChangeFromPortfolio(value);
              }}
              disabled={disabledSelect}
              id="transfer-cash-source"
            />

            <PortfolioSelect
              label="To Portfolio"
              name="toPortfolio"
              value={toPortfolio}
              options={toPortfolioOptions}
              onChange={(value) => {
                setError('');
                onChangeToPortfolio(value);
              }}
              disabled={disabledSelect}
              id="transfer-cash-destination"
            />
          </SelectContainer>

          <Form.Item name="amount" label="Input Amount">
            <InputStyled
              ref={amountInputRef}
              value={amount}
              onChange={handleChangeAmount}
              placeholder="Input Amount"
              prefix="Rp"
              type="text"
              isErrored={!!error}
            />

            {error && (
              <InputError>
                <Image src="/icon/alert/warning.svg" alt="Error" />
                <span>{error}</span>
              </InputError>
            )}

            <BalanceContainer>
              <span>Transferable Balance (Settled)</span>

              <If condition={witdrawableLoading}>
                <BalanceButtonLoader />
              </If>

              <If condition={!witdrawableLoading}>
                <BalanceButton
                  onClick={handleClickBalanceDetail}
                  className={fromPortfolio ? 'clickable' : ''}
                >
                  {formatCurrency(
                    String(
                      getRoundDownValue(
                        fromPortfolio ? availableBalance : 0,
                        0,
                      ),
                    ),
                  )}
                  <If condition={!!fromPortfolio}>
                    <Image src="/icon/chevron-right-gray.svg" alt="" />
                  </If>
                </BalanceButton>
              </If>
            </BalanceContainer>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <NextButton
              type="primary"
              disabled={disabledSubmit}
              onClick={handleSubmit}
            >
              Continue
            </NextButton>
          </Form.Item>
        </Form>
      </Container>
    </BaseSecuritiesModal>
  );
};

export default TransferCash;
